import { useQuery } from '@tanstack/react-query';
import { getOrders } from '../orders.api';
import type { OrdersListParams } from '../orders.api';

/** Klant, dienst en periode uit de kolomfilters; de drempel telt alleen voor de aandachtstelling. */
export type OrdersTabCountFilters = Pick<OrdersListParams, 'gln' | 'serviceCode' | 'from' | 'to' | 'stalenessDays'>;

/**
 * fu-6/AC2: live tellingen for the "Aandacht nodig (N)" / "Onhold (N)" tabs, scoped by the
 * SAME contextual filters (klant/dienst/periode) as the currently visible tab — but never by
 * the tab itself. Uses `pageSize: 1` (only `meta.total` is read) so this never fetches a full
 * page twice; the attention-preset union and the onhold count are already server-computed
 * (h3-1/FR16), so no extra client-side counting logic is needed here.
 */
export function useOrdersTabCounts({ stalenessDays, ...contextFilters }: OrdersTabCountFilters) {
  const attentionQuery = useQuery({
    queryKey: ['orders', 'tabCount', 'attention', { ...contextFilters, stalenessDays }],
    queryFn: () => getOrders({ ...contextFilters, stalenessDays, attention: true, page: 1, pageSize: 1 }),
    staleTime: 30 * 1000,
  });

  const onholdQuery = useQuery({
    queryKey: ['orders', 'tabCount', 'onhold', contextFilters],
    queryFn: () => getOrders({ ...contextFilters, status: 'onhold', page: 1, pageSize: 1 }),
    staleTime: 30 * 1000,
  });

  // De bewaking hoort op `meta` te staan en niet alleen op `data`: een antwoord zonder tellingen —
  // een verminderde achterkant, een tussenliggende foutpagina — legde anders het hele
  // Orders-scherm plat met "Cannot read properties of undefined (reading 'total')".
  // Gemeten 27-08-2026. Dezelfde soort fout die story fu-5 al eens ving bij glnNumbers.
  return {
    attentionCount: attentionQuery.data?.meta?.total ?? null,
    onholdCount: onholdQuery.data?.meta?.total ?? null,
  };
}
